export type GroupMember = { tableId: string; rowIndex: number };

export type MergeGroupLike = {
  _id: string;
  members: GroupMember[];
  createdAt?: number;
  updatedAt?: number;
};

export type RowLayoutItem<Group extends MergeGroupLike> =
  | { kind: "row"; rowIndex: number }
  | { kind: "group"; group: Group; rowIndices: number[] };

/**
 * Display order for one table's rows: ungrouped rows stay in place, and each merge is gathered
 * at its first member's position so its rows render together under one group header.
 */
export function buildRowLayout<Group extends MergeGroupLike>(
  tableId: string,
  rowCount: number,
  groups: Group[]
): RowLayoutItem<Group>[] {
  const groupByRow = new Map<number, Group>();
  for (const group of groups) {
    for (const member of group.members) {
      if (member.tableId === tableId) groupByRow.set(member.rowIndex, group);
    }
  }

  const items: RowLayoutItem<Group>[] = [];
  const placed = new Set<string>();
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const group = groupByRow.get(rowIndex);
    if (!group) {
      items.push({ kind: "row", rowIndex });
      continue;
    }
    if (placed.has(group._id)) continue;
    placed.add(group._id);
    const rowIndices = group.members
      .filter((member) => member.tableId === tableId && member.rowIndex < rowCount)
      .map((member) => member.rowIndex)
      .sort((a, b) => a - b);
    items.push({ kind: "group", group, rowIndices });
  }
  return items;
}

/** Member tables that are excluded from the submittal; a merge with any is skipped at generation. */
export function excludedMemberTableIds(group: MergeGroupLike, selectedTableIds: ReadonlySet<string>) {
  return [...new Set(group.members.map((member) => member.tableId))].filter(
    (tableId) => !selectedTableIds.has(tableId)
  );
}

/** The merge content generation uses; timestamps and ids don't change the output. */
export type GeneratedMerge = { name: string; directions?: string; members: GroupMember[] };

function mergeSignatures(groups: GeneratedMerge[]) {
  return groups
    .map((group) =>
      JSON.stringify([
        group.name,
        group.directions ?? "",
        group.members.map((member) => `${member.tableId}:${member.rowIndex}`).sort(),
      ])
    )
    .sort();
}

/**
 * Whether the table selection, merges, or included tables' rows changed after the submittal was
 * last generated. Merges are compared with the snapshot the generation used; submittals generated
 * before snapshots existed fall back to merge timestamps, which can't see a deleted merge.
 */
export function hasUngeneratedChanges({
  tables,
  groups,
  includedTableIds,
  generatedAt,
  generatedMerges,
}: {
  tables: { _id: string; selectedForSubmittal: boolean; rowsVersion?: number }[];
  groups: (MergeGroupLike & GeneratedMerge)[];
  includedTableIds: readonly string[];
  generatedAt: number;
  generatedMerges: GeneratedMerge[] | null;
}) {
  const included = new Set(includedTableIds);
  const selected = tables.filter((table) => table.selectedForSubmittal);
  if (selected.length !== included.size) return true;
  if (selected.some((table) => !included.has(table._id) || (table.rowsVersion ?? 0) > generatedAt)) {
    return true;
  }
  if (!generatedMerges) {
    return groups.some((group) => (group.updatedAt ?? group.createdAt ?? 0) > generatedAt);
  }
  const selectedIds = new Set(selected.map((table) => table._id));
  const activeMerges = groups.filter((group) => excludedMemberTableIds(group, selectedIds).length === 0);
  const current = mergeSignatures(activeMerges);
  const generated = mergeSignatures(generatedMerges);
  return current.length !== generated.length || current.some((signature, i) => signature !== generated[i]);
}
